import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CategoryChips } from '../../components/discovery/category-chips';
import { FilterPanel } from '../../components/discovery/filter-sheet';
import { SearchBar } from '../../components/discovery/search-bar';
import { SearchFeed } from '../../components/discovery/search-feed';
import { Icon } from '../../components/icons/icon';
import { useSearch } from '../../hooks/use-search';
import type { DiscoveryFilters } from '../../lib/discovery/search-params';
import { useDiscoveryFilters, withLocationCity } from '../../lib/discovery/use-discovery-filters';
import { useDiscoveryLocation } from '../../lib/geo/discovery-location';

/** Feed neutro por relevância: a Home não herda filtros da sessão — eles vivem nos resultados. */
const HOME_FILTERS: DiscoveryFilters = {};

/**
 * Home do consumidor — canvas T2.1 `Home` (A01) e estados A02 (F1, PRD §6.7).
 *
 * Busca no topo, 13 categorias num `.h-scroller`, feed por relevância. Funciona sem localização:
 * sem origem o feed é neutro e a distância é «—» (C4) — o GPS nunca bloqueia a entrada. O
 * carrossel do Selo fica oculto até a T2.15 ter vitrine real. No desktop (≥1024) o painel de
 * filtros fica ao lado; mexer num filtro leva aos resultados com esse filtro aplicado.
 */
export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { location, status, openPicker } = useDiscoveryLocation();
  const { apply } = useDiscoveryFilters();
  const [howOpen, setHowOpen] = useState(false);
  const locationPending = status === 'loading';
  const origin = location?.origin;
  const search = useSearch(HOME_FILTERS, origin, { enabled: !locationPending });

  const go = (filters: DiscoveryFilters) => apply(withLocationCity(filters, location));

  return (
    <div className="pc-main" data-testid="page-home" data-screen-label="home">
      <div className="pc-split">
        <FilterPanel value={HOME_FILTERS} onApply={go} withCategories={false} hasOrigin={Boolean(origin)} onChooseLocation={openPicker} />
        <div className="pc-stack">
          <section className="pc-sec" aria-labelledby="home-q-label">
            <h1 className="pc-search-label">
              <label id="home-q-label" htmlFor="home-q">{t('discovery.home.searchLabel')}</label>
            </h1>
            <SearchBar
              labelledBy="home-q-label"
              inputId="home-q"
              onSubmitText={(text) => go(text ? { query: text } : {})}
              onSelectCategory={(id) => go({ categoryIds: [id] })}
              onSelectBusiness={(slug) => navigate(`/pro/${encodeURIComponent(slug)}`)}
            />
            <CategoryChips label={t('discovery.filters.category')} selected={[]} onToggle={(id) => go({ categoryIds: [id] })} />
          </section>

          <section className="pc-sec" aria-labelledby="home-feed-title">
            <div className="pc-sec-head">
              <h2 className="pc-h2" id="home-feed-title">
                {origin ? t('discovery.home.feedTitle') : t('discovery.home.feedTitleNeutral')}
              </h2>
              <button className="hit pc-textbtn" type="button" aria-haspopup="dialog" onClick={() => setHowOpen(true)}>
                <Icon name="info" size={16} />
                <span>{t('discovery.ranking.how')}</span>
              </button>
            </div>
            {!origin && !locationPending ? (
              <p className="pc-muted flex flex-wrap items-center gap-x-1" data-testid="home-no-origin">
                <span>{t('discovery.home.noOrigin')}</span>
                <button className="hit pc-textbtn" type="button" aria-haspopup="dialog" onClick={openPicker}>
                  {t('discovery.location.choose')}
                </button>
              </p>
            ) : null}
            <SearchFeed
              search={search}
              hasOrigin={Boolean(origin)}
              locationPending={locationPending}
              label={t('discovery.home.feedLabel')}
              emptyKind="zone"
              onChooseCity={openPicker}
              howOpen={howOpen}
              onHowClose={() => setHowOpen(false)}
            />
          </section>
        </div>
      </div>
    </div>
  );
}
